/**
 * ExpiryBeforeStartWarning — "this order dies before the game starts" said out
 * loud, on the surfaces that already print the expiry (#517).
 *
 * Both venues (#557). The Kalshi trace below is the original evidence; a
 * Polymarket "GTS" is a CLOB GTD order whose `expiration` is likewise the event
 * start captured at placement, and the CLOB — which exposes no modify or amend
 * endpoint at all — has no more awareness of a reschedule than Kalshi does. The
 * venue evidence for that is recorded on #557; the arithmetic is one shared
 * predicate for both.
 *
 * Kalshi's Good to Start is not an instruction the venue re-evaluates. It is a
 * fixed expiration captured from the scheduled start at PLACEMENT, and it never
 * moves again. When an event is postponed the expiry stays where it was, so an
 * older order expires before the start now on screen while a later order on the
 * same match keeps resting. #510 traced exactly that in production: a tennis
 * order accepted at 07:57 with a 10:40 expiry was cancelled by the venue at
 * 10:40:00.000017 with no fill and no cancel of ours, while the same match's
 * later 10:50 order was still alive four minutes after that.
 *
 * The literal time was already on the card. It was not conspicuous enough to
 * read as a reason the order would disappear, which is the whole gap this
 * closes — the number was never wrong, it was just silent.
 *
 * ── Passive by construction ─────────────────────────────────────────
 * This renders text. It takes no cancel handler and no submit gate, so no
 * arrangement of props lets it cancel, replace, reprice or resize anything.
 * That is not only a style rule here: Kalshi's amend CANNOT move an
 * expiration, so the only mechanical "fix" is cancel-and-replace, which
 * forfeits queue position. #517 walls that off as a separate contract.
 *
 * ── Why `defect`, not `informational` ───────────────────────────────
 * `PassiveOperatorWarning` reserves color for what we can CONFIRM. This is
 * confirmable arithmetic on two values already on screen — the expiry and the
 * start — not a gap in what we can see. It earns the amber. Contrast
 * `EventStateWarning`'s `unknown`, which is muted precisely because nothing is
 * known to be wrong there.
 *
 * ── The live region, and why it is mounted while empty ──────────────
 * A postponement can land while the trader is sitting on the ticket, which is
 * the one moment this matters most and the one moment nobody is re-reading the
 * expiry. Same call, and the same reasoning, as `EventStateWarning`: the
 * `role="status"` wrapper stays in the tree with nothing to say so an update
 * announces as an update rather than as a new subtree. Empty it renders nothing
 * and occupies no space, so an order whose expiry is fine is geometrically
 * unchanged — every visual class lives on the INNER card for that reason.
 *
 * ── One copy, two shapes ────────────────────────────────────────────
 * The order card can host a notice box; a table cell cannot. So this module
 * owns BOTH shapes — {@link ExpiryBeforeStartWarning} for the card, {@link
 * ExpiryBeforeStartMarker} for a row — and every surface renders one of them
 * rather than assembling its own from the strings. Two venues × two surfaces
 * now show this fact; four hand-built copies of an amber glyph plus a tooltip
 * would agree only until someone edited one.
 *
 * ── It names no times, deliberately ─────────────────────────────────
 * An earlier draft opened with "expires at 10:40 AM, before the 10:50a start".
 * Both instants are ALREADY on every surface that renders this — on the card
 * the `start` row and the footer's `exp` run, in the table the start beside the
 * ticker in the market cell and the expiry in the type cell — so restating them
 * bought a line and a half of height and put the card's two existing clock
 * formats into one sentence, where the mismatch reads as two different pairs of
 * values. The box explains WHY the expiry is a problem; the surface keeps
 * showing WHAT it is.
 */

import { AlertTriangle } from 'lucide-react';
import { PassiveOperatorWarning } from './PassiveOperatorWarning';

/** The canonical phrase. The card shows it as the notice title; the table shows
 *  it as the marker's tooltip, so both surfaces name the fact identically. */
export const EXPIRY_BEFORE_START_TITLE = 'Expires before current start';

/** The table's visible marker. A row cell cannot hold {@link
 *  EXPIRY_BEFORE_START_TITLE} without pushing the column, and a tooltip alone
 *  would repeat the very mistake #517 exists to fix — a true thing on screen
 *  that nobody reads. So the cell shows this fragment of the phrase and carries
 *  the full one on hover. */
export const EXPIRY_BEFORE_START_SHORT = 'before start';

/**
 * Why the notice is showing, and the one thing the trader can still do about
 * it. The closing pointer is Brendan's own resolution on #510 — the no-expiry
 * instruction is the one that survives a schedule change — so it is guidance
 * with a source, not advice we invented at the keyboard.
 *
 * ⚠️ That pointer names the REMEDY first and the labels second (#588 review).
 * One string renders on both venues now, and "Good to Close" alone is the
 * Kalshi word for it: a Polymarket row prints `GTC`, and the CLOB's own
 * vocabulary is GTC/GTD. The remedy itself does not differ by venue — place
 * with no expiry — and G1's ticket spells that button GOOD TO CLOSE for both
 * venues, so the sentence leads with the mechanic and carries both names
 * rather than branching. Venue-specific copy would buy nothing here except two
 * sentences to keep in sync, which is the failure mode {@link
 * ExpiryBeforeStartMarker} exists to prevent.
 *
 * ⚠️ It says "before the start shown here", not "before the game" (#557). The
 * predicate compares the expiry against the start G1 is DISPLAYING, and on
 * Polymarket that start can resolve from a different venue than the order's own
 * (`updateStartTimeCache` ranks kalshi > unabated > polymarket for one game), so
 * two venues listing a game more than a minute apart can move it without the
 * schedule having changed at all. "Will expire before the game" quietly asserts
 * the displayed start IS the true start; the softer phrasing claims exactly what
 * the arithmetic proves and leaves the trader's next move unchanged.
 *
 * ⚠️ It attributes the expiry to NOTHING, and that is deliberate. An earlier
 * draft opened "Good to Start fixed this expiry" and said "the event now starts
 * later". Both are unprovable here: `expiration_ts` arrives as a bare timestamp
 * with no instruction attached (see `expiryPrecedesEventStart`), so a
 * hand-set custom expiry is indistinguishable from a GTS one, and an expiry
 * before the start does not by itself prove the schedule moved — a trader can
 * simply have chosen an earlier time. What IS provable is the arithmetic and
 * the venue's behavior, so the copy claims only that. Teaching what GTS does
 * belongs where GTS is chosen — the Settings hint and the ticket's tooltip —
 * which is also where #517 asked for it.
 */
export const EXPIRY_BEFORE_START_DETAIL =
  'The venue fixed this expiry when the order was placed and does not move it when the schedule moves, so this order is set to expire before the start shown here. An order with no expiry (GTC / Good to Close) survives schedule changes.';

/**
 * The row shape of the same finding: the notice box's glyph and amber, sized
 * for a table cell, carrying the full phrase on hover.
 *
 * A bordered three-line box would wreck a row, and a tooltip alone would repeat
 * the mistake #517 exists to fix — a true thing on screen that nobody reads. So
 * the marker is visible text plus the icon `PassiveOperatorWarning` uses for a
 * confirmed defect, and the words still come from the constants above.
 *
 * Renders nothing when the finding is false, so a healthy row is geometrically
 * unchanged. Unlike the card's box this carries no `role="status"` wrapper: a
 * row can appear and disappear from a filtered/sorted table on any tick, so an
 * announcement here would fire on churn rather than on the fact changing. The
 * card is where the live region belongs, and it has one.
 */
export function ExpiryBeforeStartMarker({
  expiresBeforeEventStart,
}: {
  /** Derived by `expiryPrecedesEventStart`; never re-derived here. */
  expiresBeforeEventStart: boolean;
}) {
  if (!expiresBeforeEventStart) return null;
  return (
    <span
      // Its own tooltip, which overrides the host cell's on hover: the expiry
      // reason matters more than restating the label beside it.
      title={`${EXPIRY_BEFORE_START_TITLE}. ${EXPIRY_BEFORE_START_DETAIL}`}
      data-testid="tif-expires-before-start"
      className="ml-1 inline-flex items-center gap-0.5 align-baseline text-[10px] font-semibold text-amber-700 dark:text-amber-300"
    >
      <AlertTriangle size={10} className="shrink-0" aria-hidden="true" />
      {EXPIRY_BEFORE_START_SHORT}
    </span>
  );
}

export function ExpiryBeforeStartWarning({
  expiresBeforeEventStart,
  className,
}: {
  /** Derived by `expiryPrecedesEventStart`; never re-derived here. */
  expiresBeforeEventStart: boolean;
  /** Spacing for the host surface only; tone is owned by the shared box.
   *  Applied to the inner card so it disappears along with it. */
  className?: string;
}) {
  return (
    // `status`, not `alert` — this sits on a live trading screen for the whole
    // life of a resting order, so it announces politely and never interrupts.
    // Mounted even while empty; see the header.
    <div role="status">
      {expiresBeforeEventStart && (
        <PassiveOperatorWarning
          severity="defect"
          title={EXPIRY_BEFORE_START_TITLE}
          explanation={EXPIRY_BEFORE_START_DETAIL}
          className={className}
          testId="expiry-before-start-warning"
        />
      )}
    </div>
  );
}
